import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, getJSON, postJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import { formatMoneyCents, parseMoneyCents } from '@/lib/money'

interface AmountLine {
  id: number
  description: string
  amount: string
}

interface SummaryRow {
  label: string
  amount: string
}

interface BreakdownRow {
  label: string
  tax: string
}

interface SettlementRow extends SummaryRow {
  id: string
  settled_on: string
}

interface InvoiceDetailResponse {
  invoice: {
    id: number
    number: number
    status: string
    origin: string
    issue_on: string
    due_on: string
    line_items: AmountLine[]
    discount_row?: SummaryRow
    tax_rows: SummaryRow[]
    subtotal?: string
    breakdown?: BreakdownRow[]
    total_tax?: string
    tax_total_label?: string
    legend?: string | null
    total: string
    settlements: SettlementRow[]
    balance: string
    payment_available: boolean
    discount_code_available: boolean
    donation_ask: DonationAsk
    currency?: string
  }
}

interface DonationAsk {
  enabled: boolean
  campaign: string | null
  preset_amounts: string[]
}

interface CheckoutResponse {
  checkout_session: {
    checkout_url: string
  }
}

type PortalInvoice = InvoiceDetailResponse['invoice']

export function MemberPortalInvoiceDetail({ invoiceId }: { invoiceId: number }) {
  const detail = useQuery<InvoiceDetailResponse>({
    queryKey: ['member-portal-invoice', invoiceId],
    queryFn: () => getJSON(`/api/v1/member_portal/invoices/${invoiceId}`),
    retry: false,
  })

  if (detail.isLoading) {
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading Invoice...
      </p>
    )
  }

  if (detail.isError || !detail.data) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404
    return (
      <Alert>
        {notFound ? 'We couldn’t find that Invoice.' : 'We couldn’t load that Invoice.'}
      </Alert>
    )
  }

  return <InvoiceDocument invoice={detail.data.invoice} />
}

function InvoiceDocument({ invoice }: { invoice: PortalInvoice }) {
  return (
    <div className="max-w-xl">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="mb-3 h-auto px-0 py-1 text-caption font-normal text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground"
      >
        <Link to="/portal/billing">← Billing</Link>
      </Button>

      <article className="overflow-hidden rounded-md border border-border bg-card">
        <header className="space-y-1 px-5 py-4">
          <div className="flex items-center gap-2">
            <h1 className="text-body font-semibold">Invoice {invoice.number}</h1>
            <Badge variant={statusVariant(invoice.status)}>{invoice.status}</Badge>
          </div>
          <Button asChild variant="outline" size="sm">
            <a
              href={`/portal/billing/invoices/${invoice.id}/print`}
              target="_blank"
              rel="noreferrer"
            >
              Print / save as PDF
            </a>
          </Button>
          <p className="text-caption text-muted-foreground">
            {invoice.origin} · Issued {displayDate(invoice.issue_on)} · Due{' '}
            {displayDate(invoice.due_on)}
          </p>
        </header>

        {invoice.status === 'Voided' && (
          <p className="border-t border-border bg-muted/40 px-5 py-2 text-body-sm">
            This Invoice is voided.
          </p>
        )}

        <div className="border-t border-border py-1">
          {invoice.line_items.map((item) => (
            <AmountRow key={item.id} label={item.description} amount={item.amount} />
          ))}
          {invoice.discount_row && (
            <AmountRow
              label={invoice.discount_row.label}
              amount={invoice.discount_row.amount}
              className="text-body-sm text-muted-foreground"
            />
          )}
          <InvoiceTaxSummary invoice={invoice} />
        </div>

        <div className="border-t border-border py-1">
          <AmountRow label="Total" amount={invoice.total} className="text-body-sm font-medium" />
          {invoice.settlements.map((settlement) => (
            <AmountRow
              key={settlement.id}
              label={`${settlement.label} · ${displayDate(settlement.settled_on)}`}
              amount={settlement.amount}
              className="text-caption text-muted-foreground"
            />
          ))}
          <AmountRow
            label="Balance due"
            amount={invoice.balance}
            className="text-body font-semibold"
          />
        </div>

        {invoice.payment_available && <InvoicePaymentFooter invoice={invoice} />}
      </article>
    </div>
  )
}

function InvoiceTaxSummary({ invoice }: { invoice: PortalInvoice }) {
  const hasTaxSummary = Boolean(
    invoice.breakdown?.length || invoice.tax_rows.length || invoice.legend,
  )
  if (!hasTaxSummary) return null

  const breakdown =
    invoice.breakdown ?? invoice.tax_rows.map((row) => ({ label: row.label, tax: row.amount }))

  return (
    <>
      {invoice.subtotal && <AmountRow label="Subtotal" amount={invoice.subtotal} />}
      {breakdown.map((row) => (
        <AmountRow key={row.label} label={row.label} amount={row.tax} />
      ))}
      {invoice.total_tax && (
        <AmountRow label={invoice.tax_total_label ?? 'Total VAT'} amount={invoice.total_tax} />
      )}
      {invoice.subtotal && <AmountRow label="Total payable" amount={invoice.total} />}
      {invoice.legend && (
        <p className="px-5 py-2 text-body-sm text-muted-foreground">{invoice.legend}</p>
      )}
    </>
  )
}

function InvoicePaymentFooter({ invoice }: { invoice: PortalInvoice }) {
  const queryClient = useQueryClient()
  const [discountCode, setDiscountCode] = React.useState('')
  const [donationAmount, setDonationAmount] = React.useState<string | null>(null)
  const checkout = useMutation({
    mutationFn: async () => {
      const donationAmountCents = donationAmount ? parseMoneyCents(donationAmount) : 0
      const quote = await postJSON<{ quote: { amount_cents: number } }>(
        '/api/v1/member_portal/billing/quote',
        { payment: { invoice_ids: [invoice.id], donation_amount_cents: donationAmountCents } },
      )
      return postJSON<CheckoutResponse>('/api/v1/member_portal/billing/checkout', {
        payment: {
          invoice_ids: [invoice.id],
          donation_amount_cents: donationAmountCents || undefined,
          quoted_amount_cents: quote.quote.amount_cents,
          idempotency_key: `invoice-${invoice.id}-${Date.now()}`,
        },
      })
    },
    onSuccess: (data) => window.location.assign(data.checkout_session.checkout_url),
  })
  const applyDiscount = useMutation({
    mutationFn: () =>
      postJSON<InvoiceDetailResponse>(
        `/api/v1/member_portal/invoices/${invoice.id}/discount_code`,
        { discount_code: { code: discountCode } },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['member-portal-invoice', invoice.id], data)
      setDiscountCode('')
    },
  })

  return (
    <footer className="border-t border-border bg-muted/40 px-5 py-3">
      {(checkout.isError || applyDiscount.isError) && (
        <Alert className="mb-3">
          {checkout.isError ? checkout.error.message : 'That Discount Code could not be applied.'}
        </Alert>
      )}
      {invoice.donation_ask.enabled && invoice.donation_ask.campaign && (
        <DonationAsk
          ask={invoice.donation_ask}
          amount={donationAmount}
          currency={invoice.currency ?? 'USD'}
          onAmountChange={setDonationAmount}
        />
      )}
      <div className="flex flex-wrap items-end justify-end gap-3">
        {invoice.discount_code_available && (
          <DiscountCodeForm
            invoiceId={invoice.id}
            code={discountCode}
            pending={applyDiscount.isPending}
            onCodeChange={setDiscountCode}
            onSubmit={() => applyDiscount.mutate()}
          />
        )}
        <Button type="button" disabled={checkout.isPending} onClick={() => checkout.mutate()}>
          {checkout.isPending
            ? 'Opening checkout...'
            : `Pay ${addMoney(invoice.balance, donationAmount, invoice.currency ?? 'USD')}`}
        </Button>
      </div>
    </footer>
  )
}

function DonationAsk({
  ask,
  amount,
  currency,
  onAmountChange,
}: {
  ask: DonationAsk
  amount: string | null
  currency: string
  onAmountChange: (amount: string | null) => void
}) {
  const [expanded, setExpanded] = React.useState(false)
  const [customAmount, setCustomAmount] = React.useState('')
  if (amount) {
    return (
      <div className="mb-3 flex items-center justify-between gap-3 border-t border-border pt-3 text-body-sm">
        <span>Donation · {ask.campaign}</span>
        <div className="flex items-center gap-3">
          <span className="tabular-nums">+{amount}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Remove donation"
            className="h-7 px-2 text-muted-foreground"
            onClick={() => {
              setExpanded(false)
              onAmountChange(null)
            }}
          >
            ✕
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-3 border-t border-border pt-3">
      <Button
        type="button"
        variant="outline"
        className="h-8 border-dashed bg-transparent px-3 text-body-sm font-normal text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((open) => !open)}
      >
        + Add a donation
      </Button>
      {expanded && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          {ask.preset_amounts.map((preset) => (
            <Button
              key={preset}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAmountChange(preset)}
            >
              {preset}
            </Button>
          ))}
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              if (customAmount.trim()) onAmountChange(formatMoneyInput(customAmount, currency))
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="custom-donation-amount">Custom donation amount</Label>
              <Input
                id="custom-donation-amount"
                inputMode="decimal"
                className="h-8 w-32"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" size="sm">
              Add
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}

function DiscountCodeForm({
  invoiceId,
  code,
  pending,
  onCodeChange,
  onSubmit,
}: {
  invoiceId: number
  code: string
  pending: boolean
  onCodeChange: (code: string) => void
  onSubmit: () => void
}) {
  return (
    <form
      className="mr-auto flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`invoice-${invoiceId}-discount-code`}>Discount Code</Label>
        <Input
          id={`invoice-${invoiceId}-discount-code`}
          className="h-9 w-40"
          value={code}
          onChange={(event) => onCodeChange(event.target.value)}
        />
      </div>
      <Button type="submit" variant="outline" size="sm" disabled={pending}>
        {pending ? 'Applying...' : 'Apply'}
      </Button>
    </form>
  )
}

function AmountRow({
  label,
  amount,
  className = 'text-body-sm',
}: {
  label: string
  amount: string
  className?: string
}) {
  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_auto] gap-6 px-5 py-3 ${className}`}>
      <span>{label}</span>
      <span className="text-right tabular-nums">{amount}</span>
    </div>
  )
}

function statusVariant(status: string): 'success' | 'warning' | 'neutral' {
  if (status === 'Paid') return 'success'
  if (status === 'Partially Paid') return 'warning'
  return 'neutral'
}

function formatMoneyInput(amount: string, currency: string) {
  return formatMoneyCents(parseMoneyCents(amount), currency)
}

function addMoney(base: string, donation: string | null, currency: string) {
  if (!donation) return base
  return formatMoneyCents(parseMoneyCents(base) + parseMoneyCents(donation), currency)
}
